import React, { useEffect, useMemo, useState } from "react";
import {
  BellRing, Trash2, Loader2, Lock, ArrowUp, ArrowDown, RotateCcw,
  CheckCircle2, AlertCircle, Percent, Activity,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchPriceAlerts,
  createPriceAlert,
  updatePriceAlert,
  deletePriceAlert,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import QuickAlertModal from "@/components/alerts/QuickAlertModal";

const fmtPrice = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

/**
 * PriceAlertsPanel — Pro-only personalized price alerts for the current
 * Analyze ticker. Lists every alert the user has + an inline composer.
 *
 * "Above" alerts fire when the price meets or exceeds the threshold.
 * "Below" alerts fire when the price meets or drops below it. Each alert is
 * one-shot (auto-disables on fire) — re-arm via RotateCcw.
 */
export const PriceAlertsPanel = ({ symbol, currentPrice, onUpsellClick }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const isPro = user?.tier === "pro";

  // Composer state
  const defaultDirection = "above";
  const defaultThreshold = useMemo(() => {
    if (!currentPrice) return "";
    return (currentPrice * 1.02).toFixed(2);
  }, [currentPrice]);
  const [direction, setDirection] = useState(defaultDirection);
  const [threshold, setThreshold] = useState(defaultThreshold);
  const [note, setNote] = useState("");
  const [pctModalOpen, setPctModalOpen] = useState(false);
  const [volModalOpen, setVolModalOpen] = useState(false);

  useEffect(() => { setThreshold(defaultThreshold); }, [defaultThreshold]);

  const reload = async () => {
    if (!isPro) return;
    setLoading(true);
    setError(null);
    try {
      const d = await fetchPriceAlerts();
      setItems(d.items || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load alerts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Filter to current symbol only, but also show recent triggered alerts globally
  const forSymbol = items.filter((a) => a.symbol === symbol);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!isPro) {
      onUpsellClick?.();
      return;
    }
    const thrNum = parseFloat(threshold);
    if (!Number.isFinite(thrNum) || thrNum <= 0) {
      toast.error("Enter a valid threshold price");
      return;
    }
    setBusy(true);
    try {
      const created = await createPriceAlert({
        symbol,
        direction,
        threshold: thrNum,
        note: note.trim(),
      });
      setItems((curr) => [created, ...curr]);
      setNote("");
      toast.success(`Alert set for ${symbol} ${direction === "above" ? "≥" : "≤"} $${thrNum}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create alert");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await deletePriceAlert(id);
      setItems((c) => c.filter((a) => a.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const rearm = async (id) => {
    try {
      const updated = await updatePriceAlert(id, { status: "active" });
      setItems((c) => c.map((a) => (a.id === id ? updated : a)));
      toast.success("Alert re-armed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not re-arm");
    }
  };

  // ----- Render: locked card for non-Pro users -----
  if (!isPro) {
    return (
      <div
        className="rounded-xl border border-[#00C805]/25 bg-gradient-to-br from-[#0a160a] to-[#070710] p-5"
        data-testid="price-alerts-locked"
      >
        <div className="flex items-center gap-2 mb-2">
          <BellRing className="w-4 h-4 text-[#00C805]" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#00C805]">
            Personalized price alerts · free
          </span>
        </div>
        <p className="text-[12.5px] text-white/70 leading-snug mb-3">
          Get a push notification the moment {symbol} crosses your level — set "above" for
          breakouts, "below" for stop-losses. Each alert is one-shot so you're never spammed.
        </p>
        {user ? (
          <button
            type="button"
            onClick={() => onUpsellClick?.()}
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md border border-[#00C805]/45 bg-[#00C805]/10 text-[#00C805] hover:bg-[#00C805]/15 transition"
            data-testid="price-alerts-upgrade"
          >
            <Lock className="w-3 h-3" /> Sign in to enable
          </button>
        ) : (
          <span className="text-[11px] text-white/50">Sign in to set alerts — free forever.</span>
        )}
      </div>
    );
  }

  // ----- Pro user: composer + list -----
  return (
    <div
      className="rounded-xl border border-white/10 bg-[#070710] p-4 sm:p-5"
      data-testid="price-alerts-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BellRing className="w-4 h-4 text-[#FFD60A]" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#FFD60A]">
            Price alerts · {symbol}
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/35">
          {items.length}/{25}
        </span>
      </div>

      {/* Composer */}
      <form
        onSubmit={submit}
        className="rounded-md border border-white/8 bg-white/[0.02] p-3 mb-3 grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-2 items-end"
      >
        <div className="inline-flex bg-[#050505] border border-white/10 rounded-md p-0.5">
          <button
            type="button"
            onClick={() => setDirection("above")}
            data-testid="price-alert-direction-above"
            className={
              "text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 rounded inline-flex items-center gap-1 transition " +
              (direction === "above"
                ? "bg-[#00C805]/15 text-[#00C805]"
                : "text-white/55 hover:text-white")
            }
          >
            <ArrowUp className="w-3 h-3" /> Above
          </button>
          <button
            type="button"
            onClick={() => setDirection("below")}
            data-testid="price-alert-direction-below"
            className={
              "text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 rounded inline-flex items-center gap-1 transition " +
              (direction === "below"
                ? "bg-[#FF3B30]/15 text-[#FF3B30]"
                : "text-white/55 hover:text-white")
            }
          >
            <ArrowDown className="w-3 h-3" /> Below
          </button>
        </div>
        <input
          type="number"
          step="0.01"
          min="0.01"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          placeholder={`Threshold (now ${fmtPrice(currentPrice)})`}
          data-testid="price-alert-threshold"
          className="bg-transparent border border-white/10 rounded-md px-2.5 py-1.5 text-[13px] num text-white outline-none focus:border-[#3FE5DC]/60 placeholder:text-white/30"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="price-alert-submit"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md border border-[#3FE5DC]/45 bg-[#3FE5DC]/10 text-[#3FE5DC] hover:bg-[#3FE5DC]/15 transition disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />}
          {busy ? "Saving" : "Set alert"}
        </button>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (e.g. 'breakout above 220 = swing long')"
          maxLength={280}
          data-testid="price-alert-note"
          className="sm:col-span-3 bg-transparent border border-white/10 rounded-md px-2.5 py-1.5 text-[12px] text-white/85 outline-none focus:border-white/30 placeholder:text-white/30"
        />
      </form>

      <div className="flex items-center justify-end mt-1 mb-2">
        <button
          type="button"
          onClick={() => setPctModalOpen(true)}
          data-testid="price-alert-pct-move-btn"
          className="text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 text-[#A98CFF] hover:text-white"
        >
          <Percent className="w-3 h-3" /> + % Move alert
        </button>
        <button
          type="button"
          onClick={() => setVolModalOpen(true)}
          data-testid="price-alert-vol-breakout-btn"
          className="text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 text-[#FF7A33] hover:text-white"
        >
          <Activity className="w-3 h-3" /> + Vol breakout
        </button>
      </div>

      <QuickAlertModal
        open={pctModalOpen}
        onClose={() => setPctModalOpen(false)}
        defaults={{
          kind: "percent_move",
          symbol,
          direction: "any",
          window: "intraday",
          percent: 2,
        }}
        title={`Alert when ${symbol} moves ±%`}
        lockKind
        onCreated={() => reload()}
      />

      <QuickAlertModal
        open={volModalOpen}
        onClose={() => setVolModalOpen(false)}
        defaults={{
          kind: "vol_breakout",
          symbol,
          direction: "above",
          vol_window: 20,
          vol_threshold_pct: 25,
        }}
        title={`Vol breakout · ${symbol}`}
        lockKind
        onCreated={() => reload()}
      />

      {loading && (
        <div className="flex items-center gap-2 text-[12px] text-white/55 py-3">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      )}
      {error && (
        <div className="rounded-md border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-2.5 text-[11.5px] text-[#FF3B30] flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {forSymbol.length === 0 ? (
            <div className="text-[12px] text-white/45 py-2">
              No alerts on {symbol} yet. Set one above.
            </div>
          ) : (
            <div className="space-y-2">
              {forSymbol.map((a) => {
                const kind = a.kind || "price_threshold";
                const isPct = kind === "percent_move";
                const isVol = kind === "vol_breakout";
                const triggered = a.status === "triggered";

                // Direction semantics differ per kind:
                //   price_threshold: above (green ↑) / below (red ↓)
                //   percent_move:    up (green ↑) / down (red ↓) / any (yellow %)
                //   vol_breakout:    above (red ↑ — vol spiking) / below (green ↓)
                const isUp = isPct
                  ? a.direction === "up" || (a.direction === "any" && (a.move_pct ?? 0) >= 0)
                  : isVol
                  ? a.direction === "above"
                  : a.direction === "above";
                const isAny = isPct && a.direction === "any";

                // Distance: % for price+vol, move_pct for percent moves.
                const distPct = isPct ? a.move_pct : a.distance_pct;
                const target = isPct ? Number(a.percent) : isVol ? Number(a.vol_threshold_pct) : null;
                const closeToTarget = !triggered && distPct != null && (
                  isPct
                    ? Math.abs(distPct) >= Math.abs(target) * 0.7
                    : isVol
                    ? Math.abs(distPct) <= Math.max(2, Math.abs(target) * 0.15)
                    : Math.abs(distPct) < 1.5
                );

                return (
                  <div
                    key={a.id}
                    data-testid={`price-alert-row-${a.id}`}
                    className={
                      "rounded-md border px-3 py-2.5 flex items-center gap-3 " +
                      (triggered
                        ? "border-[#00C805]/30 bg-[#00C805]/[0.06]"
                        : closeToTarget
                        ? "border-[#FFD60A]/30 bg-[#FFD60A]/[0.04]"
                        : "border-white/10 bg-white/[0.02]")
                    }
                  >
                    {triggered ? (
                      <CheckCircle2 className="w-4 h-4 text-[#00C805] shrink-0" />
                    ) : isAny ? (
                      <Percent className="w-4 h-4 text-[#FFD60A] shrink-0" />
                    ) : isVol ? (
                      <Activity className={`w-4 h-4 shrink-0 ${a.direction === "above" ? "text-[#FF7A33]" : "text-[#00E5FF]"}`} />
                    ) : isUp ? (
                      <ArrowUp className="w-4 h-4 text-[#00C805] shrink-0" />
                    ) : (
                      <ArrowDown className="w-4 h-4 text-[#FF3B30] shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] text-white/90 num tabular-nums">
                        {isPct ? (
                          <>
                            {a.symbol}{" "}
                            <span className="text-white/65">
                              {a.direction === "up" ? "↑ ≥" : a.direction === "down" ? "↓ ≥" : "moves ±"}
                            </span>{" "}
                            {Number(a.percent).toFixed(2)}%{" "}
                            <span className="text-[10px] uppercase tracking-[0.18em] text-white/40 ml-1">
                              {a.window || "intraday"}
                            </span>
                            {a.current_price != null && (
                              <span className="ml-2 text-white/55 text-[11px]">
                                now {fmtPrice(a.current_price)}
                                {distPct != null && (
                                  <span
                                    className="ml-1 num"
                                    style={{ color: distPct >= 0 ? "#00C805" : "#FF3B30" }}
                                  >
                                    ({distPct >= 0 ? "+" : ""}{distPct.toFixed(2)}%)
                                  </span>
                                )}
                              </span>
                            )}
                          </>
                        ) : isVol ? (
                          <>
                            {a.symbol}{" "}
                            <span className="text-white/65">
                              {a.vol_window || 20}d RV {a.direction === "above" ? "≥" : "≤"}
                            </span>{" "}
                            {Number(a.vol_threshold_pct).toFixed(1)}%
                            {a.current_vol_pct != null && (
                              <span className="ml-2 text-white/55 text-[11px]">
                                now <span className="num text-white">{a.current_vol_pct.toFixed(1)}%</span>
                                {distPct != null && (
                                  <span
                                    className="ml-1 num"
                                    style={{ color: (isUp ? distPct >= 0 : distPct <= 0) ? "#FF3B30" : "#00C805" }}
                                  >
                                    ({distPct >= 0 ? "+" : ""}{distPct.toFixed(2)}pp)
                                  </span>
                                )}
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            {a.symbol} {isUp ? "≥" : "≤"} ${Number(a.threshold).toFixed(2)}
                            {a.current_price != null && (
                              <span className="ml-2 text-white/55 text-[11px]">
                                now {fmtPrice(a.current_price)}
                                {distPct != null && (
                                  <span
                                    className="ml-1 num"
                                    style={{ color: (isUp ? distPct >= 0 : distPct <= 0) ? "#00C805" : "#FF3B30" }}
                                  >
                                    ({distPct >= 0 ? "+" : ""}{distPct.toFixed(2)}%)
                                  </span>
                                )}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      {a.note && (
                        <div className="text-[11px] text-white/55 mt-0.5 truncate">{a.note}</div>
                      )}
                      {triggered && a.triggered_at && (
                        <div className="text-[10px] text-[#00C805]/85 mt-0.5">
                          Fired @ {fmtPrice(a.triggered_price)} · {new Date(a.triggered_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                    {triggered && (
                      <button
                        type="button"
                        onClick={() => rearm(a.id)}
                        title="Re-arm"
                        data-testid={`price-alert-rearm-${a.id}`}
                        className="text-white/45 hover:text-[#3FE5DC] transition"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => remove(a.id)}
                      title="Remove"
                      data-testid={`price-alert-delete-${a.id}`}
                      className="text-white/45 hover:text-[#FF3B30] transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <div className="mt-3 pt-2 border-t border-white/8 text-[10px] uppercase tracking-[0.18em] text-white/30">
        Fires via push every 4 min cycle · one-shot · enable alerts above
      </div>
    </div>
  );
};
